import { useQuery } from '@tanstack/react-query';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import DataTable from '@/components/DataTable';
import Panel from '@/components/Panel';
import SocialDisclosureBanner from '@/components/SocialDisclosureBanner';
import SocialSourceCard from '@/components/SocialSourceCard';
import { api } from '@/lib/api';
import {
  DATA_SOURCES_ENDPOINT,
  SOCIAL_LIST_PARAMS,
  partitionDataSources,
  type DataSourceRow,
} from '@/lib/social';

// DS-5(H1): 渲染 display_status(7-label, 后端真状态)而非 db_status(粗粒度), 与 Tasks 页一致,
// 修「假绿」(部分失败/无新增/未运行 曾被显「正常」绿)。fallback default 非 orange。
const STATUS_COLOR: Record<string, string> = {
  成功: 'green',
  部分失败: 'orange',
  无新增: 'default',
  未运行: 'default',
  失败: 'red',
  已停用: 'default',
  重构中: 'warning',
};

// DS-5(H3/H4): 前端静态透明 note(不引后端字段)。
const SOURCE_NOTE: Record<string, string> = {
  yiche: 'ICE-only 部分激活：5 车型 / 2 品牌（GAC-TOYOTA 4 + GAC-HONDA 1）；NEV 暂无（合规墙）',
  dongchedi: '字节系反爬签名（x-bogus/a-bogus）阻断，暂停采集；历史数据为存档，不代表当前采集',
};

interface DataSource {
  source_id: string;
  source_name: string;
  source_type: string;
  base_url: string;
  crawl_method: string;
  anti_crawl_risk: string;
  status: string;
  display_status: string; // DS-5: 7-label 真状态(成功/部分失败/无新增/未运行/失败/已停用/重构中)
  data_quality_score: number | null;
  owner: string | null;
  last_success_time: string | null;
}

const columns: ColumnsType<DataSource> = [
  { title: '数据源', dataIndex: 'source_name', key: 'source_name', width: 180 },
  {
    title: '类型',
    dataIndex: 'source_type',
    key: 'source_type',
    width: 100,
    render: (t) => <Tag>{t}</Tag>,
  },
  { title: '抓取方式', dataIndex: 'crawl_method', key: 'crawl_method', width: 120 },
  {
    title: '反爬风险',
    dataIndex: 'anti_crawl_risk',
    key: 'anti_crawl_risk',
    width: 100,
    render: (r) => (
      <Tag color={r === '低' ? 'green' : r === '中' ? 'orange' : 'red'}>{r}</Tag>
    ),
  },
  {
    title: '状态',
    dataIndex: 'display_status',
    key: 'display_status',
    width: 100,
    render: (s) => <Tag color={STATUS_COLOR[s] ?? 'default'}>{s}</Tag>,
  },
  {
    title: '质量分',
    dataIndex: 'data_quality_score',
    key: 'data_quality_score',
    width: 100,
    render: (v) => (v != null ? `${v.toFixed(0)}` : '—'),
  },
  { title: '负责人', dataIndex: 'owner', key: 'owner', width: 100 },
  {
    title: '说明',
    key: 'note',
    width: 280,
    render: (_, row) =>
      SOURCE_NOTE[row.source_id] ? (
        <span className="text-[12px] text-slate-500">{SOURCE_NOTE[row.source_id]}</span>
      ) : (
        '—'
      ),
  },
];

export default function DataSourcesPage() {
  // PR3-B(§D4): 显式消费者 → 默认 include_social=true（无 toggle）；按 source_kind 视觉分区。
  const { data, isLoading } = useQuery({
    queryKey: ['data-sources', 'with-social'],
    queryFn: async () => {
      const res = await api.get<DataSourceRow[]>(DATA_SOURCES_ENDPOINT, { params: SOCIAL_LIST_PARAMS });
      return res.data;
    },
  });

  const { crawl, social } = partitionDataSources(data ?? []);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-[17px] lg:text-[20px] font-semibold text-slate-900 m-0">数据源管理</h1>
        {/* DS-5(H2): 副标去过期「重写后回归」—— yiche 已 ICE-only 激活, dongchedi 反爬阻断停用。 */}
        <p className="text-[12px] lg:text-[13px] text-slate-500 mt-1 mb-0">
          当前 5 源 (各源真状态见下表; yiche ICE-only 部分激活 5 车型/2 品牌, dongchedi 反爬签名阻断暂停采集)
        </p>
      </div>

      <Panel title="数据源列表（抓取源）" noPadding>
        <div className="p-2 lg:p-0">
          <DataTable<DataSource>
            rowKey="source_id"
            columns={columns}
            data={crawl as unknown as DataSource[]}
            loading={isLoading}
            pageSize={20}
            mobileMode="card"
            renderMobileCard={(row) => (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-900 text-[14px]">{row.source_name}</span>
                  <Tag color={STATUS_COLOR[row.display_status] ?? 'default'}>{row.display_status}</Tag>
                </div>
                {SOURCE_NOTE[row.source_id] && (
                  <div className="text-[11px] text-slate-500">{SOURCE_NOTE[row.source_id]}</div>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-[12px] text-slate-600">
                  <Tag>{row.source_type}</Tag>
                  <span>抓取 {row.crawl_method}</span>
                  <span>
                    反爬{' '}
                    <Tag color={row.anti_crawl_risk === '低' ? 'green' : row.anti_crawl_risk === '中' ? 'orange' : 'red'}>
                      {row.anti_crawl_risk}
                    </Tag>
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-slate-500">
                  <span>质量分 {row.data_quality_score != null ? row.data_quality_score.toFixed(0) : '—'}</span>
                  <span>负责人 {row.owner ?? '—'}</span>
                </div>
              </div>
            )}
          />
        </div>
      </Panel>

      {/* PR3-B(§D2/§D5): REDFOX 社交信号层独立分区——与抓取源视觉分区，状态来自 sla_*（非 display_status）。 */}
      {social.length > 0 && (
        <Panel title="社交信号层（REDFOX · 非 VOC/NPS）">
          <div className="space-y-4">
            <SocialDisclosureBanner source={social[0]} />
            {social.map((s) => (
              <SocialSourceCard key={s.source_id} source={s} />
            ))}
          </div>
        </Panel>
      )}
    </div>
  );
}
